import { EntityStringId, makeEntityStringId } from 'app/types/entity';
import { GuidFromBase64GuidCodec, IdentityGuidCodec, isBase64Guid, isGuid } from 'app/types/guid';
import { chain, orElse } from 'fp-ts/lib/Either';
import { pipe } from 'fp-ts/lib/pipeable';
import * as iots from 'io-ts';

export const IdentityEntityStringIdCodec: iots.Type<EntityStringId, EntityStringId> = new iots.Type(
  'IdentityEntityStringIdCodec',
  (u: unknown): u is EntityStringId => {
    return typeof u === 'string' && isGuid(u);
  },
  (i, context) => {
    return pipe(
      IdentityGuidCodec.validate(i, context),
      chain((guid) => iots.success(makeEntityStringId(guid))),
    );
  },
  (v) => v,
);

/**
 * Decoder for taking an input and turning it into an EntityStringId if viable.
 */
export const EntityStringIdInputDecoder: iots.Decoder<string, EntityStringId> = {
  name: 'EntityStringIdInputDecoder',
  validate: (i, context) => {
    return pipe(
      // Try to see if the value is an identity guid.
      IdentityEntityStringIdCodec.validate(i, context),
      // If not, try to detect if the type can be derived from a Base64 encoded guid.
      orElse(() => {
        if (typeof i === 'string' && isBase64Guid(i)) {
          return GuidFromBase64GuidCodec.validate(i, context);
        }

        return iots.failure(i, context, 'Unable to decode to an EntityStringId.');
      }),
      // Cast the guid to an entity string id.
      chain((guid) => iots.success(makeEntityStringId(guid))),
    );
  },
  decode: (i) => {
    return EntityStringIdInputDecoder.validate(i, []);
  },
};
